import { formatCurrencyWithSign } from '@/lib/utils'

/**
 * One selection a customer made on a cart bundle — a non-`isIncluded` item group they chose from,
 * reduced to the label/value pair the cart rows render (e.g. "Paper Type" / "Glossy Paper").
 *
 * Built by the `cartBundleSelections` commerce-store getter, which owns the catalog and Contentful
 * lookups behind it and their degraded fallbacks. The rows only render what it hands back, so the
 * checkout row and the cart-flyout row cannot drift on how a selection is worded.
 */
export class CartBundleSelectionItemData {
  label: string
  value: string
  /**
   * What this selection added to the bundle, from the cart item's `upchargeCostPreDiscounts`; `null`
   * when the server did not supply one. Read off the cart rather than the catalog so a later repricing
   * does not change what an existing cart appears to have agreed to. Pre-discount (BUS-001).
   */
  upcharge: number | null

  constructor(label: string, value: string, upcharge: number | null = null) {
    this.label = label
    this.value = value
    this.upcharge = upcharge
  }

  /**
   * The upcharge as the cart rows print it, or `null` when there is nothing to print. On the data
   * rather than in a component because two components render these lines. `0` renders as
   * "(Included)" rather than "($0.00)", which would read as a pricing failure.
   */
  get upchargeFormatted(): string | null {
    if (this.upcharge === null) return null

    if (this.upcharge <= 0) return '(Included)'

    // Parenthesised with a leading `+` per the cart-flyout board, so it reads as an addition to the
    // row's price rather than a second price.
    return `(${formatCurrencyWithSign(this.upcharge)})`
  }
}
